import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

interface CommandPaletteValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Opens the palette. Used by the header's search affordance. */
  show: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteValue | null>(null);

/**
 * Holds the palette's open state above the router, so anything on a page can
 * open it — the point being that ⌘K needs a visible way in. A shortcut nobody
 * can see is only fast for whoever already knows it exists.
 */
export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((previous) => !previous);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const show = useCallback(() => setOpen(true), []);
  const value = useMemo(() => ({ open, setOpen, show }), [open, show]);

  return (
    <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>
  );
}

/** Returns null outside the provider, so a component can render without it. */
export function useCommandPalette() {
  return useContext(CommandPaletteContext);
}

/**
 * ⌘ on a Mac, Ctrl on Windows and Linux — the label has to match the key the
 * reader actually presses, or the hint teaches the wrong thing.
 *
 * `navigator.userAgentData.platform` is the current answer and the one Chrome
 * keeps accurate; `navigator.platform` is deprecated and frozen, but it is
 * still correct per OS and is all Safari and Firefox offer. The user agent
 * string is the last resort.
 *
 * Read once: the operating system does not change mid-session.
 */
function detectIsApple(): boolean {
  if (typeof navigator === 'undefined') return false;

  const uaData = (navigator as Navigator & { userAgentData?: { platform?: string } })
    .userAgentData;
  if (uaData?.platform) return /mac/i.test(uaData.platform);

  if (navigator.platform) return /mac|iphone|ipad|ipod/i.test(navigator.platform);

  return /mac|iphone|ipad|ipod/i.test(navigator.userAgent ?? '');
}

export const isApplePlatform = detectIsApple();

export const shortcutModifier = isApplePlatform ? '⌘' : 'Ctrl';
